import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuickCustomerDialog } from "@/components/common";
import { Building2, User, Plus, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function LinkPicker({ open, onOpenChange, exclude, onPick, onNew }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!open) { setList(null); setQ(""); return; }
    api.get("/customers").then((r) => setList((r.data || []).filter((c) => c.is_company !== false))).catch(() => setList([]));
  }, [open]);
  const shown = (list || []).filter((c) => !exclude.includes(c.id) && (!q || norm(`${c.name} ${c.ico}`).includes(norm(q)))).slice(0, 30);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="link-company-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Pripojiť firmu</DialogTitle></DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať názov alebo IČO…" data-testid="link-company-search" />
        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {shown.map((c) => (
            <button key={c.id} type="button" onClick={() => onPick(c)} className="flex w-full items-center justify-between rounded-lg border border-border bg-card/60 p-2.5 text-left text-sm transition-colors hover:border-primary" data-testid={`link-company-option-${c.id}`}>
              <span><span className="font-semibold">{c.name}</span>{c.ico && <span className="ml-2 text-xs text-muted-foreground">IČO {c.ico}</span>}</span><Plus className="h-4 w-4 text-primary" />
            </button>
          ))}
          {list && !shown.length && <p className="py-3 text-center text-sm text-muted-foreground">Žiadna firma.</p>}
        </div>
        <Button variant="secondary" className="gap-2" onClick={() => onNew(q)} data-testid="link-company-new"><Plus className="h-4 w-4" /> Založiť novú firmu</Button>
      </DialogContent>
    </Dialog>
  );
}

export function LinkedCustomers({ customer, linked, onChanged }) {
  const navigate = useNavigate();
  const [pick, setPick] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const isCo = customer.is_company !== false;
  const link = async (c) => {
    try { await api.post(`/customers/${customer.id}/links`, { company_id: c.id }); toast.success("Firma pripojená"); setPick(false); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlink = async (c) => {
    try { await api.delete(`/customers/${customer.id}/links/${c.id}`); toast.success("Prepojenie zrušené"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (isCo && !linked.length) return null;
  const Icon = isCo ? User : Building2;
  return (
    <div className="card-surface mb-6 p-5" data-testid="linked-customers">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide">{isCo ? "Kontaktná osoba / majiteľ" : "Prepojené firmy"}</h2>
        {!isCo && <Button size="sm" variant="outline" className="ml-auto gap-1" onClick={() => setPick(true)} data-testid="link-company-open"><Plus className="h-4 w-4" /> Pripojiť firmu</Button>}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {linked.map((c) => (
          <div key={c.id} className="group flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 transition-colors hover:border-primary/60" data-testid={`linked-${c.id}`}>
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(`/zakaznici/${c.id}`)} data-testid={`linked-open-${c.id}`}>
              <div className="truncate font-semibold">{c.name}</div>
              <div className="text-xs text-muted-foreground">{c.ico ? `IČO ${c.ico}` : (c.phone || c.city || "")}</div>
            </button>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            <button type="button" title="Zrušiť prepojenie" onClick={() => unlink(c)} className="rounded p-1 text-muted-foreground hover:text-destructive" data-testid={`linked-unlink-${c.id}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
        {!linked.length && <p className="text-sm text-muted-foreground">Osoba zatiaľ nemá prepojenú firmu.</p>}
      </div>
      {!isCo && <LinkPicker open={pick} onOpenChange={setPick} exclude={linked.map((c) => c.id)} onPick={link} onNew={(q) => { setPick(false); setNewName(q); setNewOpen(true); }} />}
      {!isCo && <QuickCustomerDialog open={newOpen} onOpenChange={setNewOpen} initialName={newName} onCreated={(c) => link(c)} onPickExisting={(c) => link(c)} />}
    </div>
  );
}
